import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from 'components/AppIcon';

const TOUR_STEPS = [
  {
    id: 'summary-stats',
    targetSelector: '[aria-label="Financial summary statistics"]',
    icon: '👋',
    iconType: 'emoji',
    title: 'Welcome to FinVisAR!',
    description:
      'This is your financial command center. Get a live snapshot of your spending, budget health, and goals — all in one place.',
    position: 'below',
  },
  {
    id: 'ar-scanner',
    targetSelector: '[aria-label="AR scanner quick access"]',
    icon: 'ScanLine',
    iconType: 'lucide',
    title: 'Scan Products Instantly',
    description:
      'Point your camera at any product and instantly see how it affects your budget. Try scanning at a store before you buy!',
    position: 'below',
  },
  {
    id: 'budget-overview',
    targetSelector: '[aria-label="Budget overview and financial goals"]',
    icon: 'Wallet',
    iconType: 'lucide',
    title: 'Your Budget at a Glance',
    description:
      'Track how much you\'ve spent vs allocated for each category this month. Color-coded bars turn amber at 70% and red at 90% — so you always know where you stand.',
    position: 'above',
  },
  {
    id: 'spending-trends',
    targetSelector: '[aria-label="Spending charts and analytics"]',
    icon: 'BarChart3',
    iconType: 'lucide',
    title: 'Understand Your Spending Patterns',
    description:
      'AI-powered insights analyse your transactions and surface opportunities to save. Check back monthly to see your trends evolve.',
    position: 'above',
  },
  {
    id: 'financial-goals',
    targetSelector: '[aria-label="Budget overview and financial goals"]',
    icon: 'Trophy',
    iconType: 'lucide',
    title: 'Track Your Financial Goals',
    description:
      'Set goals like a vacation fund or emergency savings, then watch your progress in real time. FinVisAR will tell you exactly how to get there faster.',
    position: 'above',
  },
  {
    id: 'navigation',
    targetSelector: '[aria-label="Primary navigation"]',
    icon: 'Compass',
    iconType: 'lucide',
    title: 'Everything You Need, One Click Away',
    description:
      'Use the navigation to jump to Budget Manager, Spending Insights, Goals, and more. Your financial toolkit is always within reach.',
    position: 'below',
  },
];

function getElementRect(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el?.getBoundingClientRect();
  return {
    top: rect?.top + window.scrollY,
    left: rect?.left + window.scrollX,
    width: rect?.width,
    height: rect?.height,
    viewTop: rect?.top,
    viewLeft: rect?.left,
    viewBottom: rect?.bottom,
    viewRight: rect?.right,
  };
}

function ToastComplete({ onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);

  return createPortal(
    <div
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 99999,
        animation: 'tourToastIn 0.35s cubic-bezier(0.34,1.56,0.64,1) forwards',
      }}
    >
      <div
        style={{
          background: 'linear-gradient(135deg, #0F2A3A 0%, #0A1628 100%)',
          border: '1px solid rgba(0,229,255,0.4)',
          borderRadius: '12px',
          padding: '14px 24px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 8px 32px rgba(0,229,255,0.15)',
          backdropFilter: 'blur(12px)',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontSize: '20px' }}>🎉</span>
        <span style={{ color: '#F8FAFC', fontSize: '14px', fontWeight: 600 }}>Tour complete!</span>
        <span style={{ color: '#94A3B8', fontSize: '13px' }}>You're all set to explore FinVisAR.</span>
      </div>
    </div>,
    document.body
  );
}

export default function GuidedTour({ isActive, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [visible, setVisible] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const tooltipRef = useRef(null);
  const totalSteps = TOUR_STEPS?.length;

  const completeTour = useCallback(() => {
    localStorage.setItem('finvisar_tour_completed', 'true');
    setVisible(false);
    setTimeout(() => {
      onClose();
      setShowToast(true);
    }, 300);
  }, [onClose]);

  // Detect mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Escape key handler
  useEffect(() => {
    if (!isActive) return;
    const handler = (e) => {
      if (e?.key === 'Escape') completeTour();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isActive, completeTour]);

  // Scroll to target and compute positions
  useEffect(() => {
    if (!isActive) {
      setVisible(false);
      return;
    }
    setVisible(false);
    const step = TOUR_STEPS?.[currentStep];
    const el = document.querySelector(step?.targetSelector);

    const compute = () => {
      const rect = getElementRect(step?.targetSelector);
      setTargetRect(rect);
      if (rect && !isMobile) {
        computeTooltipPosition(rect, step?.position);
      }
      setTimeout(() => setVisible(true), 50);
    };

    if (el) {
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(compute, 400);
    } else {
      compute();
    }
  }, [isActive, currentStep, isMobile]);

  const computeTooltipPosition = (rect, preferredPos) => {
    const TOOLTIP_W = 340;
    const TOOLTIP_H = 220;
    const MARGIN = 16;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let style = { position: 'fixed', width: `${TOOLTIP_W}px`, zIndex: 99998 };

    const spaceBelow = vh - rect?.viewBottom;
    const spaceAbove = rect?.viewTop;
    const spaceRight = vw - rect?.viewRight;
    const spaceLeft = rect?.viewLeft;

    let pos = preferredPos;
    // Auto-adjust if not enough space
    if (pos === 'below' && spaceBelow < TOOLTIP_H + MARGIN) pos = 'above';
    if (pos === 'above' && spaceAbove < TOOLTIP_H + MARGIN) pos = 'below';
    if (pos === 'right' && spaceRight < TOOLTIP_W + MARGIN) pos = 'left';
    if (pos === 'left' && spaceLeft < TOOLTIP_W + MARGIN) pos = 'right';

    const centerX = rect?.viewLeft + rect?.width / 2;
    const centerY = rect?.viewTop + rect?.height / 2;

    if (pos === 'below') {
      style.top = `${rect?.viewBottom + MARGIN}px`;
      style.left = `${Math.max(MARGIN, Math.min(vw - TOOLTIP_W - MARGIN, centerX - TOOLTIP_W / 2))}px`;
    } else if (pos === 'above') {
      style.top = `${rect?.viewTop - TOOLTIP_H - MARGIN}px`;
      style.left = `${Math.max(MARGIN, Math.min(vw - TOOLTIP_W - MARGIN, centerX - TOOLTIP_W / 2))}px`;
    } else if (pos === 'right') {
      style.left = `${rect?.viewRight + MARGIN}px`;
      style.top = `${Math.max(MARGIN, Math.min(vh - TOOLTIP_H - MARGIN, centerY - TOOLTIP_H / 2))}px`;
    } else {
      style.left = `${rect?.viewLeft - TOOLTIP_W - MARGIN}px`;
      style.top = `${Math.max(MARGIN, Math.min(vh - TOOLTIP_H - MARGIN, centerY - TOOLTIP_H / 2))}px`;
    }

    setTooltipStyle(style);
  };

  const goNext = () => {
    if (currentStep < totalSteps - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      completeTour();
    }
  };

  const goBack = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  if (!isActive && !showToast) return null;

  const step = TOUR_STEPS?.[currentStep];

  const spotlightStyle = targetRect
    ? {
        position: 'absolute',
        top: targetRect?.top - 6,
        left: targetRect?.left - 6,
        width: targetRect?.width + 12,
        height: targetRect?.height + 12,
        borderRadius: '14px',
        boxShadow: '0 0 0 9999px rgba(13,27,42,0.82)',
        border: '2px solid rgba(0,229,255,0.5)',
        pointerEvents: 'none',
        zIndex: 99995,
        transition: 'all 0.3s ease',
      }
    : null;

  const mobileTooltipStyle = {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 99998,
    borderRadius: '20px 20px 0 0',
    width: '100%',
  };

  const finalTooltipStyle = isMobile ? mobileTooltipStyle : tooltipStyle;

  return (
    <>
      <style>{`
        @keyframes tourFadeIn {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes tourToastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
      {isActive && (
        <>
          {/* Backdrop click-to-skip */}
          <div
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 99994,
              cursor: 'default',
            }}
            onClick={completeTour}
            aria-hidden="true"
          />

          {/* Spotlight cutout */}
          {spotlightStyle && (
            <div style={{ position: 'fixed', inset: 0, zIndex: 99995, pointerEvents: 'none' }}>
              <div style={spotlightStyle} />
            </div>
          )}

          {/* Tooltip card */}
          <div
            ref={tooltipRef}
            style={{
              ...finalTooltipStyle,
              background: '#1a2a3a',
              border: '1px solid #00E5FF',
              borderRadius: isMobile ? '20px 20px 0 0' : '12px',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              padding: '20px',
              boxShadow: '0 8px 40px rgba(0,229,255,0.12), 0 2px 12px rgba(0,0,0,0.5)',
              opacity: visible ? 1 : 0,
              transform: visible ? 'scale(1)' : 'scale(0.96)',
              transition: 'opacity 0.3s ease, transform 0.3s ease',
              pointerEvents: 'all',
            }}
            onClick={(e) => e?.stopPropagation()}
          >
            {/* Step counter */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
              <span style={{ fontSize: '11px', color: '#00E5FF', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                Step {currentStep + 1} of {totalSteps}
              </span>
              <div style={{ display: 'flex', gap: '4px' }}>
                {TOUR_STEPS?.map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: i === currentStep ? '16px' : '6px',
                      height: '6px',
                      borderRadius: '3px',
                      background: i === currentStep ? '#00E5FF' : 'rgba(0,229,255,0.25)',
                      transition: 'all 0.3s ease',
                    }}
                  />
                ))}
              </div>
            </div>

            {/* Icon + Title */}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '10px' }}>
              <div
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(0,229,255,0.12)',
                  border: '1px solid rgba(0,229,255,0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
              >
                {step?.iconType === 'emoji' ? (
                  <span style={{ fontSize: '20px' }}>{step?.icon}</span>
                ) : (
                  <Icon name={step?.icon} size={20} color="#00E5FF" />
                )}
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{ color: '#F8FAFC', fontSize: '15px', fontWeight: 700, margin: 0, lineHeight: 1.3 }}>
                  {step?.title}
                </h3>
              </div>
            </div>

            {/* Description */}
            <p style={{ color: '#94A3B8', fontSize: '13px', lineHeight: 1.6, margin: '0 0 18px 0' }}>
              {step?.description}
            </p>

            {/* Navigation row */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              {/* Skip */}
              <button
                onClick={completeTour}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#475569',
                  fontSize: '12px',
                  cursor: 'pointer',
                  padding: '4px 0',
                  textDecoration: 'underline',
                  textUnderlineOffset: '2px',
                }}
              >
                Skip Tour
              </button>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {/* Back */}
                <button
                  onClick={goBack}
                  disabled={currentStep === 0}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(0,229,255,0.3)',
                    borderRadius: '8px',
                    color: currentStep === 0 ? '#334155' : '#00E5FF',
                    fontSize: '13px',
                    fontWeight: 500,
                    padding: '7px 16px',
                    cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: currentStep === 0 ? 0.4 : 1,
                  }}
                >
                  Back
                </button>

                {/* Next / Finish */}
                <button
                  onClick={goNext}
                  style={{
                    background: 'linear-gradient(135deg, #00E5FF 0%, #0891B2 100%)',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#0A1628',
                    fontSize: '13px',
                    fontWeight: 700,
                    padding: '7px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease',
                    boxShadow: '0 2px 12px rgba(0,229,255,0.3)',
                  }}
                >
                  {currentStep === totalSteps - 1 ? 'Finish' : 'Next'}
                  {currentStep < totalSteps - 1 && <Icon name="ChevronRight" size={14} color="#0A1628" />}
                  {currentStep === totalSteps - 1 && <span style={{ fontSize: '14px' }}>🎉</span>}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
      {/* Toast */}
      {showToast && <ToastComplete onDone={() => setShowToast(false)} />}
    </>
  );
}
